<template>
  <div class="page-card">
    <h2 class="page-title">分类标签管理</h2>

    <div class="toolbar">
      <div class="toolbar-left">
        <el-input
          v-model="query.keyword"
          placeholder="名称/编码"
          clearable
          style="width: 220px"
        />
        <el-select v-model="query.businessType" clearable placeholder="业务类型" style="width: 160px">
          <el-option v-for="item in businessTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <el-select v-model="query.taxonomyType" clearable placeholder="标签类型" style="width: 140px">
          <el-option v-for="item in taxonomyTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <el-select v-model="query.status" clearable placeholder="状态" style="width: 120px">
          <el-option label="启用" :value="1" />
          <el-option label="停用" :value="0" />
        </el-select>
        <el-button type="primary" @click="fetchData">查询</el-button>
      </div>
      <el-button v-if="canCreate" type="primary" @click="openDialog()">新建分类标签</el-button>
    </div>

    <el-table :data="list" border>
      <el-table-column prop="taxonomyName" label="名称" min-width="180" />
      <el-table-column prop="taxonomyCode" label="编码" min-width="160" />
      <el-table-column label="类型" width="120">
        <template slot-scope="{ row }">
          {{ taxonomyTypeLabel(row.taxonomyType) }}
        </template>
      </el-table-column>
      <el-table-column label="业务类型" min-width="180">
        <template slot-scope="{ row }">
          {{ businessTypeLabel(row.businessType) }}
        </template>
      </el-table-column>
      <el-table-column prop="parentName" label="父级" min-width="150" />
      <el-table-column label="排序" width="170">
        <template slot-scope="{ row }">
          <div class="sort-cell">
            <el-input-number v-model="row.sortNo" :min="0" :step="1" size="mini" controls-position="right" />
            <el-button v-if="canSort" type="text" @click="submitSort(row)">提交</el-button>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100">
        <template slot-scope="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'info'">
            {{ row.status === 1 ? '启用' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="updatedAt" label="更新时间" width="180" />
      <el-table-column label="操作" width="320">
        <template slot-scope="{ row }">
          <el-button v-if="canUpdate" type="text" @click="openDialog(row)">编辑</el-button>
          <el-button
            v-if="row.status === 1 && canDisable"
            type="text"
            @click="changeStatus(row, 'disable')"
          >
            停用
          </el-button>
          <el-button
            v-if="row.status !== 1 && canEnable"
            type="text"
            @click="changeStatus(row, 'enable')"
          >
            启用
          </el-button>
          <el-button v-if="canDelete" type="text" @click="removeRow(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog :title="form.id ? '编辑分类标签' : '新建分类标签'" :visible.sync="dialogVisible" width="640px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="110px">
        <el-form-item label="标签类型" prop="taxonomyType">
          <el-select v-model="form.taxonomyType" :disabled="isTagOnlyBusinessType" placeholder="请选择" style="width: 100%">
            <el-option
              v-for="item in currentTaxonomyTypeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="业务类型" prop="businessType">
          <el-select v-model="form.businessType" placeholder="请选择" style="width: 100%">
            <el-option v-for="item in businessTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="showParentFields" label="父级" prop="parentId">
          <el-select v-model="form.parentId" clearable placeholder="无父级" style="width: 100%">
            <el-option label="无父级" value="0" />
            <el-option
              v-for="item in parentOptions"
              :key="item.id"
              :label="item.taxonomyName"
              :value="String(item.id)"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="标签编码" prop="taxonomyCode">
          <el-input v-model="form.taxonomyCode" />
        </el-form-item>
        <el-form-item label="标签名称" prop="taxonomyName">
          <el-input v-model="form.taxonomyName" />
        </el-form-item>
        <el-form-item v-if="showImageFields" label="图标地址">
          <el-input v-model="form.iconUrl" />
        </el-form-item>
        <el-form-item v-if="showImageFields" label="封面地址">
          <el-input v-model="form.coverUrl" />
        </el-form-item>
        <el-form-item label="排序值">
          <el-input-number v-model="form.sortNo" :min="0" :step="1" style="width: 100%" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="form.description" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { hasPermission } from '../../../utils/auth'
import {
  createOpsTaxonomy,
  deleteOpsTaxonomy,
  disableOpsTaxonomy,
  enableOpsTaxonomy,
  getOpsTaxonomies,
  sortOpsTaxonomy,
  updateOpsTaxonomy
} from '../../../api/opsTaxonomy'

const taxonomyTypeOptions = [
  { label: '分类', value: 1 },
  { label: '标签', value: 2 }
]

const businessTypeOptions = [
  { label: '通告分类', value: 1 },
  { label: '艺人技能标签', value: 2 },
  { label: '艺人风格标签', value: 3 },
  { label: '首页导航分类', value: 4 },
  { label: '活动标签', value: 5 },
  { label: '会员标签', value: 6 },
  { label: '才艺中心分类', value: 7 }
]
const hiddenBusinessTypeValues = [4, 5, 6]
const visibleBusinessTypeOptions = businessTypeOptions.filter(item => !hiddenBusinessTypeValues.includes(item.value))

function getDefaultForm() {
  return {
    id: '',
    taxonomyType: 1,
    businessType: undefined,
    parentId: '0',
    taxonomyCode: '',
    taxonomyName: '',
    iconUrl: '',
    coverUrl: '',
    description: '',
    sortNo: 0
  }
}

export default {
  name: 'OpsTaxonomyPage',
  data() {
    return {
      list: [],
      allTaxonomies: [],
      query: {
        keyword: '',
        status: undefined,
        businessType: undefined,
        taxonomyType: undefined
      },
      dialogVisible: false,
      form: getDefaultForm(),
      taxonomyTypeOptions,
      businessTypeOptions: visibleBusinessTypeOptions,
      rules: {
        taxonomyType: [{ required: true, message: '请选择标签类型', trigger: 'change' }],
        businessType: [{ required: true, message: '请选择业务类型', trigger: 'change' }],
        taxonomyCode: [{ required: true, message: '请输入标签编码', trigger: 'blur' }],
        taxonomyName: [{ required: true, message: '请输入标签名称', trigger: 'blur' }]
      }
    }
  },
  computed: {
    canCreate() {
      return hasPermission('ops_taxonomy:create')
    },
    canUpdate() {
      return hasPermission('ops_taxonomy:update')
    },
    canEnable() {
      return hasPermission('ops_taxonomy:enable')
    },
    canDisable() {
      return hasPermission('ops_taxonomy:disable')
    },
    canSort() {
      return hasPermission('ops_taxonomy:sort')
    },
    canDelete() {
      return hasPermission('ops_taxonomy:update') || hasPermission('ops_taxonomy:delete')
    },
    isTagOnlyBusinessType() {
      return this.form.businessType === 1 || this.form.businessType === 2
    },
    currentTaxonomyTypeOptions() {
      return this.isTagOnlyBusinessType
        ? this.taxonomyTypeOptions.filter(item => item.value === 2)
        : this.taxonomyTypeOptions
    },
    showParentFields() {
      return !this.isTagOnlyBusinessType
    },
    showImageFields() {
      return !this.isTagOnlyBusinessType
    },
    parentOptions() {
      return this.allTaxonomies.filter(item => {
        if (this.form.id && String(item.id) === String(this.form.id)) {
          return false
        }
        if (!this.form.businessType) {
          return true
        }
        return item.businessType === this.form.businessType
      })
    }
  },
  watch: {
    'form.businessType'(value) {
      if (value === 1 || value === 2) {
        this.form.taxonomyType = 2
        this.form.parentId = '0'
        this.form.iconUrl = ''
        this.form.coverUrl = ''
      }
    }
  },
  created() {
    this.fetchData()
    this.fetchAllTaxonomies()
  },
  methods: {
    taxonomyTypeLabel(value) {
      const target = this.taxonomyTypeOptions.find(item => item.value === value)
      return target ? target.label : '-'
    },
    businessTypeLabel(value) {
      const target = businessTypeOptions.find(item => item.value === value)
      return target ? target.label : '-'
    },
    async fetchData() {
      this.list = await getOpsTaxonomies(this.query)
    },
    async fetchAllTaxonomies() {
      this.allTaxonomies = await getOpsTaxonomies({})
    },
    openDialog(row) {
      this.dialogVisible = true
      this.form = row
        ? {
            ...getDefaultForm(),
            ...row,
            id: row.id ? String(row.id) : '',
            parentId: row.parentId && row.parentId !== '0' ? String(row.parentId) : '0'
          }
        : getDefaultForm()
      if (this.form.businessType === 1 || this.form.businessType === 2) {
        this.form.taxonomyType = 2
        this.form.parentId = '0'
        this.form.iconUrl = ''
        this.form.coverUrl = ''
      }
    },
    submitForm() {
      this.$refs.formRef.validate(async valid => {
        if (!valid) return
        const payload = {
          ...this.form,
          parentId: this.showParentFields && this.form.parentId && this.form.parentId !== '0'
            ? Number(this.form.parentId)
            : 0,
          iconUrl: this.showImageFields ? this.form.iconUrl : '',
          coverUrl: this.showImageFields ? this.form.coverUrl : '',
          taxonomyType: this.isTagOnlyBusinessType ? 2 : this.form.taxonomyType
        }
        if (this.form.id) {
          await updateOpsTaxonomy(this.form.id, payload)
        } else {
          await createOpsTaxonomy(payload)
        }
        this.dialogVisible = false
        await this.fetchData()
        await this.fetchAllTaxonomies()
      })
    },
    async changeStatus(row, type) {
      if (type === 'enable') {
        await enableOpsTaxonomy(row.id)
      } else {
        await disableOpsTaxonomy(row.id)
      }
      await this.fetchData()
      await this.fetchAllTaxonomies()
    },
    async removeRow(row) {
      await this.$confirm(`确认删除 ${row.taxonomyName} 吗？`, '提示', {
        type: 'warning'
      })
      await deleteOpsTaxonomy(row.id)
      await this.fetchData()
      await this.fetchAllTaxonomies()
    },
    async submitSort(row) {
      await sortOpsTaxonomy(row.id, {
        businessType: row.businessType,
        sortNo: row.sortNo
      })
      await this.fetchData()
      await this.fetchAllTaxonomies()
    }
  }
}
</script>

<style scoped>
.sort-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
